/** As mensagens da conversa, com rolagem até a última. A do usuário à direita,
 *  com o tinte de superfície e, ao passar o mouse, editar e reenviar e copiar;
 *  a do assistente sem moldura, em markdown, com os cartões de etapa antes, o
 *  SQL dobrado depois e o rodapé ao passar o mouse, que desde 07/09/2026 também
 *  apaga o turno. Etapas e consultas obedecem às preferências do menu do
 *  painel; enquanto a resposta chega, as etapas aparecem sempre. */
import { Suspense, lazy, useEffect, useRef, useState, type KeyboardEvent } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { alpha } from '@mui/material/styles'
import { AlertCircle, Check, Copy, Pencil, RotateCcw, SendHorizontal, ThumbsDown, ThumbsUp, Trash2 } from 'lucide-react'
import { useI18n, type UseI18n } from '@/core/i18n/useI18n'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { useAvaliarMensagem } from './api'
import { useAssistente, useConversaDoAssistente, type MensagemVista } from './AssistenteProvider'
import { ConsultaSql } from './ConsultaSql'
import { EstadoVazio } from './EstadoVazio'
import { EtapasDaResposta } from './EtapaCartao'
import type { ErroDoFluxo } from './fluxoReducer'
import { Pensando } from './Pensando'
import { useCopiar } from './useCopiar'

// O leitor de markdown pesa 147 KB e só serve quando chega a primeira resposta:
// fora do pacote inicial, que toda tela baixa (05/10/2026). Enquanto ele desce, a
// resposta aparece como texto corrido, sem pular de lugar.
const MarkdownResposta = lazy(() => import('./MarkdownResposta').then((m) => ({ default: m.MarkdownResposta })))

const PROXIMO_DO_FIM_PX = 80

/** Esqueleto de linhas com as medidas do texto que vai chegar. */
export function EsqueletoDeLinhas({ larguras = ['100%', '86%', '64%'] }: { larguras?: string[] }) {
  return (
    <Box sx={{ display: 'grid', gap: 1 }} aria-hidden>
      {larguras.map((largura, i) => (
        <Box key={i} className="assistente-esqueleto" sx={{ height: 14, width: largura }} />
      ))}
    </Box>
  )
}

function EsqueletoDeMensagens() {
  return (
    <Box sx={{ display: 'grid', gap: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Box className="assistente-esqueleto" sx={{ height: 36, width: '60%', borderRadius: '0 16px 0 16px' }} />
      </Box>
      <EsqueletoDeLinhas />
    </Box>
  )
}

function BolhaDoUsuario({ mensagem }: { mensagem: MensagemVista }) {
  const { t } = useI18n()
  const { editarPergunta } = useConversaDoAssistente()
  const { copiado, copiar } = useCopiar()
  const botao = { size: 'small' as const, sx: { color: 'text.secondary', '&:hover': { color: 'text.primary' } } }
  return (
    <Box
      className="anim-fade-up"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        '&:hover .assistente-acoes-usuario, &:focus-within .assistente-acoes-usuario': { opacity: 1 },
        '@media (hover: none)': { '& .assistente-acoes-usuario': { opacity: 1 } },
      }}
    >
      <Box
        role="article"
        aria-label={t('assistente.usuario.mensagem')}
        sx={{
          maxWidth: '88%',
          px: 1.5,
          py: 1,
          borderRadius: '0 16px 0 16px',
          bgcolor: 'var(--color-surface-strong)',
          color: 'text.primary',
          fontSize: '0.875rem',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {mensagem.texto}
      </Box>
      <Box className="assistente-acoes-usuario" sx={{ display: 'flex', gap: 0.25, mt: 0.25, mr: -0.75, opacity: 0, transition: 'opacity var(--transition-quick)' }}>
        <Tooltip title={t('assistente.usuario.editar')}>
          <IconButton {...botao} onClick={() => editarPergunta(mensagem.texto)} aria-label={t('assistente.usuario.editar')}>
            <Pencil size={14} strokeWidth={2} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t(copiado ? 'assistente.resposta.copiado' : 'assistente.usuario.copiar')}>
          <IconButton {...botao} onClick={() => copiar(mensagem.texto)} aria-label={t('assistente.usuario.copiar')} sx={{ ...botao.sx, color: copiado ? 'success.main' : 'text.secondary' }}>
            {copiado ? <Check size={14} strokeWidth={2.4} /> : <Copy size={14} strokeWidth={2} />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}

function fraseDoErro(t: UseI18n['t'], erro: ErroDoFluxo): string {
  switch (erro.code) {
    case 'NETWORK_ERROR':
      return t('assistente.erro.rede')
    case 'FLUXO_INTERROMPIDO':
      return t('assistente.erro.interrompido')
    case 'CRIAR_CONVERSA':
      return t('assistente.erro.criarConversa')
    default:
      return erro.mensagem || t('assistente.erro.generico')
  }
}

function CartaoDeErro({ erro, onTentar }: { erro: ErroDoFluxo; onTentar: () => void }) {
  const { t } = useI18n()
  return (
    <Box
      className="anim-fade-down"
      role="alert"
      sx={{
        mt: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.5,
        py: 1.25,
        borderRadius: '0 12px 0 12px',
        bgcolor: (tema) => alpha(tema.palette.error.main, tema.palette.mode === 'dark' ? 0.16 : 0.08),
        color: (tema) => (tema.palette.mode === 'dark' ? tema.palette.error.main : tema.palette.error.dark),
      }}
    >
      <AlertCircle size={16} strokeWidth={2} aria-hidden />
      <Typography sx={{ flex: 1, fontSize: '0.8125rem', fontWeight: 500 }}>{fraseDoErro(t, erro)}</Typography>
      <Button size="small" variant="outlined" color="inherit" onClick={onTentar} startIcon={<RotateCcw size={13} />} sx={{ flex: 'none', minHeight: 32 }}>
        {t('assistente.resposta.tentar')}
      </Button>
    </Box>
  )
}

function RodapeDaResposta({ mensagem, onTentar }: { mensagem: MensagemVista; onTentar: () => void }) {
  const { t } = useI18n()
  const { copiado, copiar } = useCopiar()
  const { apagarTurno } = useConversaDoAssistente()
  const avaliar = useAvaliarMensagem()
  const [avaliacao, setAvaliacao] = useState<'util' | 'naoUtil' | null>(null)
  const [comentarioAberto, setComentarioAberto] = useState(false)
  const [comentario, setComentario] = useState('')
  const [apagando, setApagando] = useState(false)
  const [apagandoAgora, setApagandoAgora] = useState(false)
  // Mensagem ainda sem uuid do servidor não tem onde registrar a avaliação,
  // nem o que apagar lá.
  const podeAvaliar = !mensagem.uuid.startsWith('local-') && mensagem.uuid !== 'fluxo'

  async function confirmarApagar() {
    setApagandoAgora(true)
    try {
      await apagarTurno(mensagem.uuid)
    } finally {
      setApagandoAgora(false)
      setApagando(false)
    }
  }

  function marcarUtil() {
    setAvaliacao('util')
    setComentarioAberto(false)
    avaliar.mutate({ mensagemUuid: mensagem.uuid, corpo: { util: true } })
  }
  function enviarNaoUtil() {
    setAvaliacao('naoUtil')
    setComentarioAberto(false)
    avaliar.mutate({ mensagemUuid: mensagem.uuid, corpo: { util: false, comentario: comentario.trim() || undefined } })
    setComentario('')
  }
  function aoTeclarComentario(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      enviarNaoUtil()
    } else if (e.key === 'Escape') {
      e.stopPropagation()
      setComentarioAberto(false)
    }
  }

  const botao = { size: 'small' as const, sx: { color: 'text.secondary', '&:hover': { color: 'text.primary' } } }

  return (
    <Box sx={{ mt: 0.5 }}>
      <Box className="assistente-rodape" sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: -0.75, opacity: comentarioAberto || avaliacao ? 1 : 0, transition: 'opacity var(--transition-quick)' }}>
        <Tooltip title={t(copiado ? 'assistente.resposta.copiado' : 'assistente.resposta.copiar')}>
          <IconButton {...botao} onClick={() => copiar(mensagem.texto)} aria-label={t('assistente.resposta.copiar')} sx={{ ...botao.sx, color: copiado ? 'success.main' : 'text.secondary' }}>
            {copiado ? <Check size={15} strokeWidth={2.4} /> : <Copy size={15} strokeWidth={2} />}
          </IconButton>
        </Tooltip>
        {podeAvaliar && (
          <>
            <Tooltip title={t('assistente.resposta.util')}>
              <IconButton {...botao} onClick={marcarUtil} aria-pressed={avaliacao === 'util'} aria-label={t('assistente.resposta.util')} sx={{ ...botao.sx, color: avaliacao === 'util' ? 'primary.main' : 'text.secondary' }}>
                <ThumbsUp size={15} strokeWidth={2} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('assistente.resposta.naoUtil')}>
              <IconButton {...botao} onClick={() => setComentarioAberto((a) => !a)} aria-pressed={avaliacao === 'naoUtil'} aria-expanded={comentarioAberto} aria-label={t('assistente.resposta.naoUtil')} sx={{ ...botao.sx, color: avaliacao === 'naoUtil' ? 'primary.main' : 'text.secondary' }}>
                <ThumbsDown size={15} strokeWidth={2} />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Tooltip title={t('assistente.resposta.tentar')}>
          <IconButton {...botao} onClick={onTentar} aria-label={t('assistente.resposta.tentar')}>
            <RotateCcw size={15} strokeWidth={2} />
          </IconButton>
        </Tooltip>
        {podeAvaliar && (
          <Tooltip title={t('assistente.turno.apagar')}>
            <IconButton {...botao} onClick={() => setApagando(true)} aria-label={t('assistente.turno.apagar')}>
              <Trash2 size={15} strokeWidth={2} />
            </IconButton>
          </Tooltip>
        )}
        {avaliacao && !comentarioAberto && (
          <Typography sx={{ ml: 0.5, fontSize: '0.6875rem', color: 'text.disabled' }}>{t('assistente.resposta.avaliada')}</Typography>
        )}
      </Box>
      {comentarioAberto && (
        <Box className="anim-fade-down" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            onKeyDown={aoTeclarComentario}
            placeholder={t('assistente.resposta.oQueFaltou')}
            slotProps={{ htmlInput: { 'aria-label': t('assistente.resposta.oQueFaltou'), 'data-assistente-composer': 'true' } }}
            sx={{ '& .MuiInputBase-root': { fontSize: '0.8125rem' } }}
          />
          <IconButton size="small" color="primary" onClick={enviarNaoUtil} aria-label={t('assistente.resposta.enviarAvaliacao')}>
            <SendHorizontal size={15} strokeWidth={2.2} />
          </IconButton>
        </Box>
      )}
      <ConfirmDialog
        open={apagando}
        title={t('assistente.turno.apagarTitulo')}
        message={t('assistente.turno.apagarTexto')}
        confirmLabel={t('assistente.turno.apagar')}
        danger
        loading={apagandoAgora}
        onConfirm={() => void confirmarApagar()}
        onClose={() => setApagando(false)}
      />
    </Box>
  )
}

function RespostaDoAssistente({ mensagem, onTentar }: { mensagem: MensagemVista; onTentar: () => void }) {
  const { t } = useI18n()
  const { mostrarEtapas, mostrarConsultas } = useAssistente()
  const status = mensagem.fluxo?.status
  const emAndamento = status === 'aguardando' || status === 'fluindo'
  // Enquanto não há texto, o sinal de trabalho fica na tela: no instante em
  // que a pergunta sai, entre uma etapa e outra, e enquanto o modelo escreve a
  // resposta depois da última consulta.
  const preparando = emAndamento && !mensagem.texto
  const erro = status === 'erro' ? mensagem.fluxo?.erro ?? null : null
  const parada = status === 'parado'
  const temCorpo = Boolean(mensagem.texto) || mensagem.consultas.length > 0

  return (
    <Box
      role="article"
      aria-label={t('assistente.assistente.mensagem')}
      aria-busy={emAndamento}
      sx={{
        '&:hover .assistente-rodape, &:focus-within .assistente-rodape': { opacity: 1 },
        '@media (hover: none)': { '& .assistente-rodape': { opacity: 1 } },
      }}
    >
      {(emAndamento || mostrarEtapas) && <EtapasDaResposta etapas={mensagem.etapas} emAndamento={emAndamento} />}
      {preparando && <Pensando />}
      {mensagem.texto && (
        <Suspense fallback={<Typography component="div" sx={{ whiteSpace: 'pre-wrap' }}>{mensagem.texto}</Typography>}>
          <MarkdownResposta texto={mensagem.texto} fluindo={status === 'fluindo'} />
        </Suspense>
      )}
      {mostrarConsultas && mensagem.consultas.map((consulta, i) => <ConsultaSql key={i} consulta={consulta} />)}
      {parada && (
        <Typography sx={{ mt: 1, fontSize: '0.75rem', color: 'text.disabled' }}>{t('assistente.resposta.parada')}</Typography>
      )}
      {erro && <CartaoDeErro erro={erro} onTentar={onTentar} />}
      {!emAndamento && !erro && temCorpo && <RodapeDaResposta mensagem={mensagem} onTentar={onTentar} />}
    </Box>
  )
}

/** A pergunta que antecede a resposta na posição `indice`, para o Tentar de novo
 *  de uma resposta antiga refazer a pergunta certa, e não a última digitada. */
function perguntaAnterior(mensagens: MensagemVista[], indice: number): string | null {
  for (let i = indice - 1; i >= 0; i -= 1) {
    if (mensagens[i].papel === 'usuario') return mensagens[i].texto
  }
  return null
}

export function MensagemLista() {
  const { t } = useI18n()
  const { conversaAtual, mensagens, carregando, erroAoCarregar, fluxo, enviar, tentarDeNovo } = useConversaDoAssistente()
  const reduzido = useMediaQuery('(prefers-reduced-motion: reduce)')
  const rolagemRef = useRef<HTMLDivElement | null>(null)
  const presoAoFimRef = useRef(true)
  const quantidade = mensagens.length

  // Mensagem nova, do usuário ou o começo da resposta: o painel rola até ela.
  useEffect(() => {
    const el = rolagemRef.current
    if (!el) return
    presoAoFimRef.current = true
    el.scrollTo({ top: el.scrollHeight, behavior: reduzido ? 'auto' : 'smooth' })
  }, [quantidade, conversaAtual, reduzido])

  // Texto chegando: acompanha só enquanto o usuário estiver perto do fim, para
  // não puxar de volta quem subiu para reler.
  useEffect(() => {
    const el = rolagemRef.current
    if (!el || !presoAoFimRef.current) return
    el.scrollTop = el.scrollHeight
  }, [fluxo.texto, fluxo.etapas, fluxo.consultas])

  function aoRolar() {
    const el = rolagemRef.current
    if (!el) return
    presoAoFimRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < PROXIMO_DO_FIM_PX
  }

  // Conversa sem mensagem nenhuma é o mesmo nada do painel recém-aberto, e
  // acontece de verdade: o teto diário barra a primeira pergunta depois de a
  // conversa já existir, e o painel ficava numa tela em branco sem saudação nem
  // sugestão. Enquanto carrega, o esqueleto; se falhou ao abrir, a frase do erro.
  if (quantidade === 0 && !carregando && !erroAoCarregar) return <EstadoVazio />

  return (
    <Box
      ref={rolagemRef}
      onScroll={aoRolar}
      aria-busy={carregando}
      sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}
    >
      {carregando && <EsqueletoDeMensagens />}
      {erroAoCarregar && !carregando && quantidade === 0 && (
        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{t('assistente.conversa.carregarErro')}</Typography>
      )}
      {mensagens.map((mensagem, indice) => {
        if (mensagem.papel === 'usuario') return <BolhaDoUsuario key={mensagem.uuid} mensagem={mensagem} />
        // A resposta em curso refaz o turno no lugar; uma antiga vira turno novo.
        const pergunta = perguntaAnterior(mensagens, indice)
        const onTentar = mensagem.fluxo
          ? tentarDeNovo
          : () => { if (pergunta) enviar(pergunta) }
        return <RespostaDoAssistente key={mensagem.uuid} mensagem={mensagem} onTentar={onTentar} />
      })}
    </Box>
  )
}
